// [Project] > [Status] - where this project stands: its row from the deployment
// dashboard, then one card per specialist switched on for it. The Manager coordinates
// and keeps no status of its own.
//
// Deliberately thin: the status, who owes the next move, and links through to the
// detail (the card's name, its rows and its counts). Everything else lives one click away on the agent's own tab, because a
// page that carries every field is one nobody reads at a glance.
// See docs/Y2026-Q3/design-project-agent-status-page.md.
import { Link, useParams } from "react-router-dom";

import { useProjectAgentStatus } from "../api";
import type { ProjectAgentStatus, ProjectAgentStatusList } from "../api";
import type { PortfolioRow } from "../api/hooks/useAgentPortfolio";
import { useDashboard } from "../api/hooks/useDashboard";
import { BriefingFrame } from "../components/BriefingFrame";
import { useBriefingView } from "../utils/briefingView";
import { DeploymentTable } from "../components/DeploymentTable";
import { actionCounts } from "../utils/nextActions";
import { areaLines } from "../utils/readinessAreas";
import { statusOpener } from "../utils/statusOpener";
import { TopNextActions } from "../components/TopNextActions";
import { ReadyToBuildOverview } from "../components/ReadyToBuild";
import {
  LifecycleChip,
  OwnerChip,
  StatusForCard,
  VerdictChip,
  VerdictReason,
  resolveVerdictItem,
} from "../components/PortfolioRowParts";
import { reviewAttrs } from "../utils/reviewState";
import { isStoodDown } from "../utils/standDown";
import { projectSpecialistPath } from "./learningAgentPaths";
import styles from "./ProjectStatusTab.module.css";

// An agent that keeps a status row has a tab of its own; any other links to its state.
// Cards alternate between two columns, keeping the first two side by side; `order`
// restores the sequence when they stack. An agent not involved in the project sits at
// the foot of the left column.
const placeCards = (
  agents: ProjectAgentStatus[],
): { entry: ProjectAgentStatus; order: number; inColumn: number }[] => {
  const working = agents.filter((a) => !isStoodDown(a.row));
  const notInvolved = agents.filter((a) => isStoodDown(a.row));
  return [
    ...working.map((entry, i) => ({ entry, order: i, inColumn: i % 2 })),
    ...notInvolved.map((entry, i) => ({ entry, order: working.length + i, inColumn: 0 })),
  ];
};

// Building readiness's card: its opener, unless it restates the verdict's reason printed
// just above it, then each area as its tile on the Building readiness page says it -
// not the status's own area lines, which are written separately and drift from them.
const cardStatus = (row: PortfolioRow, keepsReadiness: boolean): string => {
  if (!keepsReadiness || !row.readiness) return row.status_details;
  const opener =
    row.on_track?.verdict && row.on_track.reason ? "" : statusOpener(row.status_details);
  return [opener, areaLines(row.readiness)].filter(Boolean).join("\n");
};

const detailPath = (projectId: string, entry: ProjectAgentStatus): string =>
  projectSpecialistPath(projectId, entry.agent);

// One specialist's headline (design 7.1): its verdict, stage and who holds its top next
// action; its status; its top next action and any missed external deadline; and how many
// actions it holds. Each links through to the specialist's page.
const AgentCard = ({
  entry,
  projectId,
  data,
}: {
  entry: ProjectAgentStatus;
  projectId: string;
  data: ProjectAgentStatusList;
}) => {
  const row = entry.row;
  const lists = entry.lists;
  const page = detailPath(projectId, entry);
  const counts = lists ? actionCounts(lists.actions, data) : null;
  // Its agent's proposals to move an action's date, answered on the action; a tracked
  // date's is answered, and counted, under Agent feedback.
  const moves = Object.values(data.date_moves ?? {}).filter(
    (move) => move.agent === entry.agent && move.item_kind === "action",
  ).length;
  const status = row ? cardStatus(row, entry.keeps_readiness) : "";
  return (
    <section
      className={styles.card}
      {...reviewAttrs("specialist_summary", entry.label, {
        agent: entry.agent,
      })}
    >
      <header className={styles.cardHead}>
        <h3>
          <Link to={page}>{entry.label}</Link>
        </h3>
        {row?.on_track && <VerdictChip onTrack={row.on_track} />}
        {row && <LifecycleChip lifecycle={row.lifecycle} />}
        {lists && (
          <OwnerChip
            who={lists.next_move.who}
            lifecycle={row?.lifecycle}
            overdueFrom={lists.next_move.overdue_from}
            move={lists.next_move}
          />
        )}
      </header>

      {entry.attention && <p className={styles.attention}>{entry.attention}</p>}

      {!row && !lists ? (
        <div className={styles.block}>
          <p className={styles.muted}>
            Nothing reported yet. The next scheduled pass that works this
            project fills this in.
          </p>
        </div>
      ) : (
        <>
          {row?.on_track?.verdict && (
            <div className={styles.block}>
              <VerdictReason
                onTrack={row.on_track}
                found={resolveVerdictItem(row.on_track, {
                  data,
                  agent: entry.agent,
                  projectId,
                })}
              />
            </div>
          )}
          {status.trim() && (
            <div className={styles.block}>
              <StatusForCard text={status} omitLabels={[]} />
            </div>
          )}
          {lists && (
            <div className={styles.block}>
              <TopNextActions
                entry={entry}
                data={data}
                projectId={projectId}
              />
            </div>
          )}
          {counts && (
            <div className={styles.block}>
              <p className={styles.counts}>
                <Link
                  to={entry.keeps_readiness ? page : `${page}#next-actions`}
                >
                  {counts.total} total action{counts.total === 1 ? "" : "s"}
                </Link>{" "}
                · {counts.onUs} on us · {counts.external} on external ·{" "}
                {counts.dueSoon} due soon · {counts.overdue} overdue
                {moves > 0 && (
                  <>
                    {" · "}
                    <Link
                      to={
                        entry.keeps_readiness ? page : `${page}#next-actions`
                      }
                    >
                      {moves} date move{moves === 1 ? "" : "s"} to approve
                    </Link>
                  </>
                )}
              </p>
              {entry.feedback_waiting > 0 && (
                <p className={styles.counts}>
                  <Link to={`${page}#agent-feedback`}>
                    {entry.feedback_waiting} agent feedback action
                    {entry.feedback_waiting === 1 ? "" : "s"}
                  </Link>
                </p>
              )}
            </div>
          )}
        </>
      )}
    </section>
  );
};

// The same row the dashboard shows, so the two cannot disagree about this site, with
// each party's asset manager under its name. It has no history yet, so an earlier version
// leaves it out rather than show today's.
const SummaryRow = ({ projectId }: { projectId: string }) => {
  const { data } = useDashboard(projectId);
  if (!data || data.projects.length === 0) return null;
  return (
    <div {...reviewAttrs("dashboard_row", "Project summary")}>
      <DeploymentTable
        attributes={data.attributes}
        rows={data.projects}
        linkNames={false}
        split
        sitePage
      />
    </div>
  );
};

export const ProjectStatusTab = () => {
  const { projectId } = useParams();
  const decoded = projectId ? decodeURIComponent(projectId) : "";
  return (
    <BriefingFrame projectId={decoded} level="specialist_summary">
      <StatusOverview projectId={decoded} />
    </BriefingFrame>
  );
};

export const StatusOverview = ({
  projectId: decoded,
}: {
  projectId: string;
}) => {
  const { version } = useBriefingView();
  const { data, isLoading, error } = useProjectAgentStatus(
    decoded || null,
    version,
  );

  if (isLoading) return <p className={styles.muted}>Loading the status…</p>;
  if (error)
    return (
      <p className={styles.error}>
        Could not load this project&apos;s agent status.
      </p>
    );
  if (!data) return null;

  return (
    <div className={styles.tab}>
      {version === null && <SummaryRow projectId={decoded} />}
      <ReadyToBuildOverview projectId={decoded} data={data} />
      {data.agents.length > 0 && (
        <h2 className={styles.sectionHeading}>Per-specialist status</h2>
      )}
      {data.agents.length === 0 && (
        <p className={styles.muted}>
          No agent works this project yet. Enrol it from Settings and the next
          scheduled pass will write a status.
        </p>
      )}
      {data.agents.length > 0 && (
        // Two columns that stack independently, as on a specialist's page, so a tall
        // card never leaves a gap beside it (see placeCards).
        <div className={styles.cards}>
          {[0, 1].map((column) => (
            <div key={column} className={styles.stack}>
              {placeCards(data.agents).map(({ entry, order, inColumn }) =>
                inColumn === column ? (
                  <div key={entry.agent} style={{ order }}>
                    <AgentCard entry={entry} projectId={decoded} data={data} />
                  </div>
                ) : null,
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
